import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle, X, Download, Eye, Clock, FileText, Ban, Play, Upload } from 'lucide-react';
import { downloadFile } from '../utils/fileUtils';

const FinanceDashboard = () => {
  const navigate = useNavigate();
  const [prs, setPrs] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedPr, setSelectedPr] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holdReasonType, setHoldReasonType] = useState('');
  const [scheduledPaymentDays, setScheduledPaymentDays] = useState('');
  const [isTdsApplicable, setIsTdsApplicable] = useState('No');
  const [tdsPercentage, setTdsPercentage] = useState('');
  
  // Pagination & Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [costCenterFilter, setCostCenterFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      const response = await axios.get(`${API}/purchase-requests`, {
        headers: getAuthHeader()
      });
      setPrs(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase requests');
    }
  };

  // Helper function to get advance payment status badge
  const getAdvanceStatusBadge = (pr, showActualStatus = false) => {
    if (!pr.is_advance_payment) return null;
    
    // Don't show badge if status is none or undefined
    if (!pr.advance_payment_status || pr.advance_payment_status === 'none') return null;
    
    // For partially paid advance requests
    if (pr.is_partial_paid && pr.advance_payment_status === 'paid') {
      // In details view, always show "Advance Paid"
      if (showActualStatus) {
        return (
          <Badge className="bg-emerald-100 text-emerald-800 text-xs w-auto inline-flex">
            Advance Paid
          </Badge>
        );
      }
      
      // In table view:
      // If status is still 'approved' (Step 2: before final invoice upload), show "Approved PP"
      if (pr.status === 'approved') {
        return (
          <Badge className="bg-blue-100 text-blue-800 text-xs w-auto inline-flex">
            Approved PP
          </Badge>
        );
      }
      // If status is pending_finance_approval or finance_approved (Step 3/4: after final invoice), show "Advance Paid"
      return (
        <Badge className="bg-emerald-100 text-emerald-800 text-xs w-auto inline-flex">
          Advance Paid
        </Badge>
      );
    }
    
    const statusConfig = {
      'pending_ppa': { label: 'Pending PPA', color: 'bg-yellow-100 text-yellow-800' },
      'approved_pp': { label: 'Approved PP', color: 'bg-blue-100 text-blue-800' },
      'ppa_approved': { label: 'PP Approved', color: 'bg-green-100 text-green-800' },
      'ppa_rejected': { label: 'PP Rejected', color: 'bg-red-100 text-red-800' },
      'advance_paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' },
      'paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' }
    };
    
    const config = statusConfig[pr.advance_payment_status];
    if (!config) return null;
    
    return (
      <Badge className={`${config.color} text-xs inline-flex w-auto`}>
        {config.label}
      </Badge>
    );
  };

  // Helper function to preserve file extension from S3 key
  const getFilenameWithExtension = (s3Key, defaultName) => {
    if (!s3Key) return defaultName;
    
    // Extract original filename from S3 key (e.g., "proforma/uuid_invoice.png" -> "invoice.png")
    const parts = s3Key.split('/');
    const filename = parts[parts.length - 1];
    
    // Extract extension from original filename
    const extensionMatch = filename.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '.pdf';
    
    // Return default name with correct extension
    return defaultName.replace(/\.[^.]*$/, '') + extension;
  };

  // Get unique cost centers for filter dropdown
  const getUniqueCostCenters = () => {
    const costCenters = new Set();
    prs.forEach(pr => {
      if (pr.cost_centre) {
        costCenters.add(pr.cost_centre);
      }
    });
    return Array.from(costCenters).sort();
  };

  const uniqueCostCenters = getUniqueCostCenters();

  const fetchVendorDetails = async (vendorId) => {
    try {
      const response = await axios.get(`${API}/vendors/${vendorId}`, {
        headers: getAuthHeader()
      });
      setVendorDetails(response.data);
    } catch (error) {
      // Silently fail - vendor details are optional
      console.error('Failed to fetch vendor details:', error);
      setVendorDetails(null);
    }
  };

  // Filter PRs based on active/history tab
  const getFilteredPrs = () => {
    let filtered = [];
    
    if (activeTab === 'active') {
      // Active: regular finance approvals + advance requests pending PPA
      filtered = prs.filter(pr => 
        pr.status === 'pending_finance_approval' ||
        (pr.is_advance_payment && pr.advance_payment_status === 'pending_ppa')
      );
    } else {
      // History: show all PRs that Finance has acted upon (not approver rejections)
      // Include: finance_approved, rejected by FINANCE, additional_info_requested, paid
      // For advance payments: include approved_pp and partially paid (but NOT if status is pending_finance_approval)
      filtered = prs.filter(pr => 
        pr.status === 'finance_approved' || 
        (pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance')) ||
        pr.status === 'additional_info_requested' ||
        pr.status === 'paid' ||
        (pr.is_advance_payment && pr.advance_payment_status === 'approved_pp') ||
        (pr.is_advance_payment && pr.is_partial_paid && pr.status !== 'pending_finance_approval')
      );
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(pr => 
        pr.requested_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.cost_centre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.po_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply cost center filter
    if (costCenterFilter !== 'all') {
      filtered = filtered.filter(pr => pr.cost_centre === costCenterFilter);
    }

    // Apply vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(pr => pr.vendor_name === vendorFilter);
    }

    return filtered;
  };

  const getUniqueVendors = () => {
    const vendors = new Set();
    prs.forEach(pr => {
      if (pr.vendor_name) {
        vendors.add(pr.vendor_name);
      }
    });
    return Array.from(vendors).sort();
  };

  const filteredPrs = getFilteredPrs();
  const uniqueVendors = getUniqueVendors();
  const totalPages = Math.ceil(filteredPrs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPrs = filteredPrs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, costCenterFilter, vendorFilter, activeTab]);

  const openActionDialog = async (pr, action) => {
    setSelectedPr(pr);
    setActionType(action);
    setRejectionReason('');
    setVendorDetails(null); // Reset vendor details
    setDialogOpen(true);
    // Fetch vendor details asynchronously without blocking
    if (pr.vendor_id) {
      fetchVendorDetails(pr.vendor_id);
    }
  };

  const openViewDialog = async (pr) => {
    setSelectedPr(pr);
    setVendorDetails(null); // Reset vendor details
    setViewDialogOpen(true);
    // Fetch vendor details asynchronously without blocking
    if (pr.vendor_id) {
      fetchVendorDetails(pr.vendor_id);
    }
  };

  // Handle PPA (Partial Payment Approval) for advance requests
  const handleApprovePPA = async (pr) => {
    if (!window.confirm('Approve partial payment for this advance request?')) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/purchase-requests/${pr.id}/approve-ppa`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Partial payment approved successfully');
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve PPA');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPPA = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/reject-ppa`,
        { action: 'reject_ppa', reason: rejectionReason.trim() },
        { headers: getAuthHeader() }
      );
      toast.success('Partial payment rejected');
      setDialogOpen(false);
      setRejectionReason('');
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject PPA');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    if (actionType === 'schedule_payment') {
      if (!scheduledPaymentDays || scheduledPaymentDays <= 0) {
        toast.error('Please enter valid number of days (greater than 0)');
        return;
      }
      
      setLoading(true);
      try {
        await axios.post(
          `${API}/purchase-requests/${selectedPr.id}/schedule-payment`,
          { 
            days: parseInt(scheduledPaymentDays),
            is_tds_applicable: isTdsApplicable,
            tds_percentage: isTdsApplicable === 'Yes' ? parseFloat(tdsPercentage) || 0 : 0
          },
          { headers: getAuthHeader() }
        );
        toast.success('Payment scheduled successfully');
        setDialogOpen(false);
        setScheduledPaymentDays('');
        setIsTdsApplicable('No');
        setTdsPercentage('');
        fetchPendingReviews();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to schedule payment');
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const endpoint = `${API}/purchase-requests/${selectedPr.id}/finance-action`;
      
      const requestBody = actionType === 'approve'
        ? { 
            action: 'approve',
            is_tds_applicable: isTdsApplicable,
            tds_percentage: isTdsApplicable === 'Yes' ? parseFloat(tdsPercentage) || 0 : 0
          }
        : { action: 'reject', additional_info_request: rejectionReason };

      await axios.post(
        endpoint,
        requestBody,
        { headers: getAuthHeader() }
      );

      toast.success(
        actionType === 'approve' 
          ? 'Purchase request approved successfully'
          : 'Purchase request rejected'
      );
      setDialogOpen(false);
      setIsTdsApplicable('No');
      setTdsPercentage('');
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const openHoldDialog = (pr, action) => {
    setSelectedPr(pr);
    setActionType(action);
    setHoldReason('');
    setHoldReasonType('');
    setHoldDialogOpen(true);
  };

  const handleHoldAction = async () => {
    if (actionType === 'hold' && !holdReason.trim()) {
      toast.error('Hold reason is required');
      return;
    }

    if (actionType === 'hold' && !holdReasonType) {
      toast.error('Hold reason type is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/hold`,
        {
          action: actionType,
          reason: actionType === 'hold' ? holdReason : null,
          hold_reason_type: actionType === 'hold' ? holdReasonType : null
        },
        { headers: getAuthHeader() }
      );

      toast.success(
        actionType === 'hold'
          ? 'Request put on hold'
          : 'Request hold removed'
      );
      setHoldDialogOpen(false);
      setHoldReason('');
      setHoldReasonType('');
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (pr) => {
    // Determine status badge
    let badgeClass = 'bg-gray-100 text-gray-800';
    let statusText = pr.status;

    // For advance payments with pending PPA or approved_pp (before payment), set appropriate status
    if (pr.is_advance_payment && (pr.advance_payment_status === 'pending_ppa' || pr.advance_payment_status === 'approved_pp')) {
      // Will show request type badge + advance status badge, no main status
      badgeClass = '';
      statusText = '';
    } else if (pr.is_advance_payment && pr.is_partial_paid && pr.status === 'approved') {
      // For partially paid advance requests with status='approved' (Step 2: after advance paid, before final invoice)
      // Hide main badge, show only "Approved PP" advance badge
      badgeClass = '';
      statusText = '';
    } else if (pr.status === 'pending_finance_approval') {
      badgeClass = 'bg-yellow-100 text-yellow-800';
      statusText = 'Pending Finance Review';
    } else if (pr.status === 'finance_approved') {
      badgeClass = 'bg-green-100 text-green-800';
      statusText = 'Approved';
    } else if (pr.status === 'rejected') {
      badgeClass = 'bg-red-100 text-red-800';
      statusText = 'Rejected';
    } else if (pr.status === 'additional_info_requested') {
      badgeClass = 'bg-orange-100 text-orange-800';
      statusText = 'Additional Info Requested';
    } else if (pr.status === 'paid') {
      badgeClass = 'bg-green-100 text-green-800';
      statusText = 'Approved'; // Finance sees 'Approved' instead of 'Approved For Payment'
    }

    // Return badges with request type indicator
    return (
      <div className="flex flex-col gap-1 items-start">
        {pr?.is_advance_payment && (
          <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">AR</span>
          </Badge>
        )}
        {!pr?.is_advance_payment && !pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">PR</span>
          </Badge>
        )}
        {pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <Upload className="w-3 h-3" />
            <span className="text-xs font-semibold">UI</span>
          </Badge>
        )}
        {statusText && <Badge className={`${badgeClass} inline-flex w-auto`}>{statusText}</Badge>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
              Finance Review
            </h1>
          </div>
        </div>
      </nav>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs for Active and History */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'active'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Active ({prs.filter(pr => 
                pr.status === 'pending_finance_approval' ||
                (pr.is_advance_payment && pr.advance_payment_status === 'pending_ppa')
              ).length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              History ({prs.filter(pr => 
                pr.status === 'finance_approved' || 
                (pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance')) ||
                pr.status === 'additional_info_requested' ||
                pr.status === 'paid' ||
                (pr.is_advance_payment && pr.advance_payment_status === 'approved_pp') ||
                (pr.is_advance_payment && pr.is_partial_paid && pr.status !== 'pending_finance_approval')
              ).length})
            </button>
          </nav>
        </div>

        <Card data-testid="finance-request-list">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">
              {activeTab === 'active' ? 'Active Reviews' : 'History'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filter and Search Controls */}
            <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-3 items-center flex-1">
                <input
                  type="text"
                  placeholder="Search by requester, vendor, entity, cost center, PO..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
                />
                <select
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Vendors</option>
                  {uniqueVendors.map(vendor => (
                    <option key={vendor} value={vendor}>{vendor}</option>
                  ))}
                </select>
                <select
                  value={costCenterFilter}
                  onChange={(e) => setCostCenterFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Cost Centers</option>
                  {uniqueCostCenters.map(cc => (
                    <option key={cc} value={cc}>{cc}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Requested By</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Vendor Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Entity</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Center</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">PO Number</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">View</th>
                    {activeTab === 'active' && (
                      <>
                        <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Schedule Payment</th>
                        <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Approve</th>
                        <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Reject</th>
                        <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Hold</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentPrs.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'active' ? "12" : "8"} className="px-4 py-8 text-center text-gray-500">
                        No requests found
                      </td>
                    </tr>
                  ) : (
                    currentPrs.map((pr) => (
                      <tr key={pr.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {pr.requested_by}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {pr.vendor_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pr.entity_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pr.cost_centre}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {(() => {
                            // For advance payment requests
                            if (pr.is_advance_payment) {
                              // In History tab, show total amount for all advance payments (both paid and approved)
                              if (activeTab === 'history') {
                                return `₹${(pr.total_amount || pr.base_amount || 0).toLocaleString()}`;
                              }
                              // 1st time request (advance not yet paid) - show advance amount with balance
                              if (!pr.is_partial_paid) {
                                const advanceAmount = pr.advance_amount || 0;
                                const totalAmount = pr.total_amount || pr.base_amount || 0;
                                const balance = totalAmount - advanceAmount;
                                return (
                                  <div>
                                    <div>₹{advanceAmount.toLocaleString()}</div>
                                    <div className="text-xs text-amber-600 font-semibold mt-1">
                                      Bal: ₹{balance.toLocaleString()}
                                    </div>
                                  </div>
                                );
                              }
                              // 2nd request in Active tab (after advance paid) - show total amount with payable balance
                              else {
                                const totalAmount = pr.total_amount || pr.base_amount || 0;
                                const advanceAmount = pr.advance_amount || 0;
                                const payableBalance = (pr.payable_amount || totalAmount) - advanceAmount;
                                return (
                                  <div>
                                    <div>₹{totalAmount.toLocaleString()}</div>
                                    <div className="text-xs text-amber-600 font-semibold mt-1">
                                      Bal: ₹{payableBalance.toLocaleString()}
                                    </div>
                                  </div>
                                );
                              }
                            }
                            // Regular purchase requests - show total amount
                            return `₹${(pr.total_amount || pr.base_amount || 0).toLocaleString()}`;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pr.po_number || 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(pr)}
                          {getAdvanceStatusBadge(pr) && (
                            <div className="mt-1">{getAdvanceStatusBadge(pr)}</div>
                          )}
                          {pr.hold_status && (
                            <div className="mt-1">
                              <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Button
                            onClick={() => openViewDialog(pr)}
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                        {activeTab === 'active' && (
                          <>
                            <td className="px-3 py-3 text-center">
                              <Button
                                onClick={() => openActionDialog(pr, 'schedule_payment')}
                                size="sm"
                                variant="outline"
                                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                disabled={pr.hold_status}
                              >
                                <Clock className="w-4 h-4" />
                              </Button>
                            </td>
                            <td className="px-3 py-3 text-center">
                              {pr.is_advance_payment && pr.advance_payment_status === 'pending_ppa' ? (
                                <Button
                                  onClick={() => handleApprovePPA(pr)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  disabled={pr.hold_status || loading}
                                  title="Approve Partial Payment (PPA)"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => openActionDialog(pr, 'approve')}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  disabled={pr.hold_status}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {pr.is_advance_payment && pr.advance_payment_status === 'pending_ppa' ? (
                                <Button
                                  onClick={() => openActionDialog(pr, 'reject_ppa')}
                                  size="sm"
                                  variant="destructive"
                                  disabled={pr.hold_status || loading}
                                  title="Reject Partial Payment (PPA)"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => openActionDialog(pr, 'reject')}
                                  size="sm"
                                  variant="destructive"
                                  disabled={pr.hold_status}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {pr.hold_status ? (
                                <Button
                                  onClick={() => openHoldDialog(pr, 'unhold')}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Play className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => openHoldDialog(pr, 'hold')}
                                  size="sm"
                                  variant="destructive"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {filteredPrs.length > 0 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredPrs.length)} of {filteredPrs.length} results
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        return page === 1 || 
                               page === totalPages || 
                               (page >= currentPage - 1 && page <= currentPage + 1);
                      })
                      .map((page, idx, arr) => (
                        <span key={page}>
                          {idx > 0 && arr[idx - 1] !== page - 1 && (
                            <span className="px-2 text-gray-500">...</span>
                          )}
                          <Button
                            onClick={() => setCurrentPage(page)}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            className="min-w-[2.5rem]"
                          >
                            {page}
                          </Button>
                        </span>
                      ))
                    }
                  </div>
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve Purchase Request' : 
               actionType === 'reject' ? 'Reject Purchase Request' : 
               actionType === 'reject_ppa' ? 'Reject Partial Payment (PPA)' :
               actionType === 'schedule_payment' ? 'Schedule Payment' : 'Request Additional Information'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' ? 'Review and approve this purchase request' : 
               actionType === 'reject' ? 'Provide a reason for rejection' : 
               actionType === 'reject_ppa' ? 'Provide a reason for rejecting the partial payment approval' :
               actionType === 'schedule_payment' ? 'Enter number of days after which payment should be made' : 
               'Specify what additional information is needed'}
            </DialogDescription>
          </DialogHeader>

          {selectedPr && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Requested By</Label>
                  <p className="text-sm font-medium">{selectedPr.requested_by}</p>
                </div>
                <div>
                  <Label>Cost Center</Label>
                  <p className="text-sm font-medium">{selectedPr.cost_centre}</p>
                </div>
                <div>
                  <Label>Tax Inclusive</Label>
                  <p className="text-sm font-medium">{selectedPr.inclusive_tax || 'No'}</p>
                </div>
                <div>
                  <Label>PO Number</Label>
                  <p className="text-sm font-medium">{selectedPr.po_number || 'N/A'}</p>
                </div>
                <div>
                  <Label>Base Amount</Label>
                  <p className="text-sm font-medium">₹{(selectedPr.base_amount || 0).toLocaleString()}</p>
                </div>
                {selectedPr.inclusive_tax === 'Yes' && (
                  <>
                    <div>
                      <Label>Tax Amount</Label>
                      <p className="text-sm font-medium">₹{(selectedPr.tax_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <Label>Total Amount</Label>
                      <p className="text-sm font-medium text-green-700">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                    </div>
                  </>
                )}
              </div>

              <div>
                <Label>Reason for Request</Label>
                <p className="text-sm text-gray-700">{selectedPr.reason}</p>
              </div>

              {vendorDetails && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Vendor Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label>Vendor Name</Label>
                      <p className="font-medium">{vendorDetails.legal_entity_name}</p>
                    </div>
                    <div>
                      <Label>GST Number</Label>
                      <p className="font-medium">{vendorDetails.gst_number}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedPr.tax_invoice_path && (
                <div className="border-t pt-4">
                  <Label>Tax Invoice</Label>
                  <Button
                    onClick={() => downloadFile(selectedPr.tax_invoice_path, 'Tax Invoice')}
                    variant="outline"
                    size="sm"
                    className="mt-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Tax Invoice
                  </Button>
                </div>
              )}

              {/* Show advance payment details for approve action if partially paid */}
              {(actionType === 'approve') && selectedPr?.is_advance_payment && selectedPr?.is_partial_paid && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-amber-900 mb-3">Advance Payment Details</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600">Total Amount</p>
                      <p className="font-bold text-gray-900">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Advance Paid</p>
                      <p className="font-bold text-green-700">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Remaining Amount</p>
                      <p className="font-bold text-orange-700">₹{((selectedPr.total_amount || selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-800 mt-2">
                    <strong>Note:</strong> This approval is for the remaining payment of ₹{((selectedPr.total_amount || selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}
                  </p>
                </div>
              )}

              {/* TDS Fields for Approve and Schedule Payment - Hide for Pending PPA (advance payment approval) */}
              {(actionType === 'approve' || actionType === 'schedule_payment') && 
               !(selectedPr?.is_advance_payment && selectedPr?.advance_payment_status === 'pending_ppa') && (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-semibold text-gray-900">TDS Configuration</h4>
                  
                  <div>
                    <Label>Is TDS Applicable? <span className="text-red-500">*</span></Label>
                    <select
                      value={isTdsApplicable}
                      onChange={(e) => {
                        setIsTdsApplicable(e.target.value);
                        if (e.target.value === 'No') {
                          setTdsPercentage('');
                        }
                      }}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>

                  {isTdsApplicable === 'Yes' && (
                    <>
                      <div>
                        <Label>TDS Percentage (%) <span className="text-red-500">*</span></Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={tdsPercentage}
                          onChange={(e) => setTdsPercentage(e.target.value)}
                          placeholder="Enter TDS percentage (e.g., 10, 2, 1)"
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter the TDS percentage to be deducted</p>
                      </div>

                      {selectedPr && tdsPercentage && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h5 className="font-semibold text-blue-900 mb-2">Calculation Preview:</h5>
                          <div className="space-y-1 text-sm">
                            <p className="text-blue-800">
                              Base Amount: ₹{(selectedPr.base_amount || 0).toLocaleString()}
                            </p>
                            {selectedPr.tax_amount > 0 && (
                              <p className="text-blue-800">
                                Tax Amount: ₹{(selectedPr.tax_amount || 0).toLocaleString()}
                              </p>
                            )}
                            <p className="text-blue-800">
                              Total Amount: ₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}
                            </p>
                            {/* Show Advance Paid if this is an advance payment with advance already paid */}
                            {selectedPr.is_advance_payment && selectedPr.is_partial_paid && (
                              <p className="text-green-700 font-medium">
                                Advance Already Paid: ₹{(selectedPr.advance_amount || 0).toLocaleString()}
                              </p>
                            )}
                            <p className="text-blue-800 font-semibold">
                              TDS Deduction ({tdsPercentage}% on Base): ₹{((selectedPr.base_amount || 0) * (parseFloat(tdsPercentage) / 100)).toLocaleString(undefined, {maximumFractionDigits: 2})}
                            </p>
                            <p className="text-blue-900 font-bold border-t border-blue-300 pt-2 mt-2">
                              Payable Amount: ₹{(() => {
                                const totalAmount = selectedPr.total_amount || selectedPr.base_amount || 0;
                                const tdsDeduction = (selectedPr.base_amount || 0) * (parseFloat(tdsPercentage) / 100);
                                const advancePaid = (selectedPr.is_advance_payment && selectedPr.is_partial_paid) ? (selectedPr.advance_amount || 0) : 0;
                                return (totalAmount - advancePaid - tdsDeduction).toLocaleString(undefined, {maximumFractionDigits: 2});
                              })()}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {(actionType === 'reject' || actionType === 'reject_ppa') && (
                <div>
                  <Label>Rejection Reason</Label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={4}
                    placeholder="Enter reason for rejection..."
                  />
                </div>
              )}

              {actionType === 'schedule_payment' && (
                <>
                  {/* Show advance payment details if partially paid */}
                  {selectedPr?.is_advance_payment && selectedPr?.is_partial_paid && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-amber-900 mb-3">Advance Payment Details</h4>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="text-gray-600">Total Amount</p>
                          <p className="font-bold text-gray-900">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Advance Paid</p>
                          <p className="font-bold text-green-700">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Remaining Amount</p>
                          <p className="font-bold text-orange-700">₹{((selectedPr.total_amount || selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label>Schedule Payment After (Days)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={scheduledPaymentDays}
                      onChange={(e) => setScheduledPaymentDays(e.target.value)}
                      placeholder="Enter number of days (e.g., 7, 15, 30)"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedPr?.is_advance_payment && selectedPr?.is_partial_paid 
                        ? `Remaining payment of ₹${((selectedPr.total_amount || selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()} will be scheduled ${scheduledPaymentDays} days from today`
                        : `Payment will be scheduled ${scheduledPaymentDays} days from today`
                      }
                    </p>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button
                  onClick={() => setDialogOpen(false)}
                  variant="outline"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={actionType === 'reject_ppa' ? handleRejectPPA : handleAction}
                  disabled={loading}
                  className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 
                            (actionType === 'reject' || actionType === 'reject_ppa') ? 'bg-red-600 hover:bg-red-700' : 
                            actionType === 'schedule_payment' ? 'bg-blue-600 hover:bg-blue-700' :
                            'bg-orange-600 hover:bg-orange-700'}
                >
                  {loading ? 'Processing...' : 
                   actionType === 'approve' ? 'Approve' : 
                   (actionType === 'reject' || actionType === 'reject_ppa') ? 'Reject' : 
                   actionType === 'schedule_payment' ? 'Schedule Payment' : 'Request Info'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Request Details</DialogTitle>
            <DialogDescription>Complete information about this purchase request</DialogDescription>
          </DialogHeader>

          {selectedPr && (
            <div className="space-y-6">
              {/* Status Badge - Direct display at top */}
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedPr)}
                {selectedPr.hold_status && (
                  <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                )}
              </div>

              {/* Hold Reason if present */}
              {selectedPr.hold_status && selectedPr.hold_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-800 mb-2">Hold Reason:</p>
                  {selectedPr.hold_reason_type && (
                    <p className="text-sm font-medium text-red-800 mb-2">Type: {selectedPr.hold_reason_type}</p>
                  )}
                  <p className="text-sm text-red-700">{selectedPr.hold_reason}</p>
                  {selectedPr.held_by && selectedPr.held_at && (
                    <p className="text-xs text-red-600 mt-2">
                      Held by: {selectedPr.held_by} on {new Date(selectedPr.held_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Request Information */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Request Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Requested By</Label>
                    <p className="text-sm font-medium text-gray-900">{selectedPr.requested_by}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Entity</Label>
                    <p className="text-sm font-medium text-gray-900">{selectedPr.entity_name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Vendor Name</Label>
                    <p className="text-sm font-medium text-gray-900">{vendorDetails?.legal_entity_name || selectedPr.vendor_name || 'Loading...'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Cost Center</Label>
                    <p className="text-sm font-medium text-gray-900">{selectedPr.cost_centre}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Tax Inclusive</Label>
                    <p className="text-sm font-medium text-gray-900">{selectedPr.inclusive_tax || 'No'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Base Amount</Label>
                    <p className="text-sm font-medium text-gray-900">₹{(selectedPr.base_amount || 0).toLocaleString()}</p>
                  </div>
                  {selectedPr.inclusive_tax === 'Yes' && (
                    <>
                      <div>
                        <Label className="text-gray-600">Tax Amount</Label>
                        <p className="text-sm font-medium text-gray-900">₹{(selectedPr.tax_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <Label className="text-gray-600">Total Amount</Label>
                        <p className="text-sm font-medium text-green-700">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                      </div>
                    </>
                  )}
                  {selectedPr.is_tds_applicable === 'Yes' && (
                    <>
                      <div>
                        <Label className="text-gray-600">TDS Percentage</Label>
                        <p className="text-sm font-medium text-gray-900">{selectedPr.tds_percentage || 0}%</p>
                      </div>
                      <div>
                        <Label className="text-gray-600">Payable Amount</Label>
                        <p className="text-sm font-medium text-blue-700 font-semibold">
                          ₹{(selectedPr.payable_amount || 0).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                  <div>
                    <Label className="text-gray-600">PO Number</Label>
                    <p className="text-sm font-medium text-gray-900">{selectedPr.po_number || 'Not yet generated'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Approved By</Label>
                    <p className="text-sm font-medium text-gray-900">{selectedPr.approved_by || 'Pending'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Created At</Label>
                    <p className="text-sm font-medium text-gray-900">{selectedPr.created_at ? new Date(selectedPr.created_at).toLocaleString() : 'N/A'}</p>
                  </div>
                  {selectedPr.finance_approved_at && (
                    <div>
                      <Label className="text-gray-600">Finance Approved Date</Label>
                      <p className="text-sm font-medium text-gray-900">{new Date(selectedPr.finance_approved_at).toLocaleDateString()}</p>
                    </div>
                  )}
                  {selectedPr.scheduled_payment_days && selectedPr.scheduled_payment_date && (
                    <div>
                      <Label className="text-gray-600">Schedule Payment After</Label>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedPr.scheduled_payment_days} days : {new Date(selectedPr.scheduled_payment_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reason */}
              <div>
                <Label className="text-gray-600">Reason for Request</Label>
                <p className="text-sm text-gray-900 mt-1 p-3 bg-gray-50 rounded">{selectedPr.reason}</p>
              </div>

              {/* Advance Payment Details */}
              {selectedPr.is_advance_payment && (
                <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-amber-900">Advance Payment Details</h3>
                    {getAdvanceStatusBadge(selectedPr, true)}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-amber-700">Total Amount</Label>
                      <p className="text-sm font-bold text-amber-900">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-amber-700">Advance Payment</Label>
                      <p className="text-sm font-bold text-green-900 bg-green-50 p-2 rounded">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-amber-700">Payment Status</Label>
                      <p className="text-sm font-medium text-amber-900">
                        {selectedPr.is_partial_paid ? 'Advance Paid' : 'Advance Pending'}
                      </p>
                    </div>
                    {selectedPr.advance_approved_by && (
                      <div>
                        <Label className="text-amber-700">PPA Approved By</Label>
                        <p className="text-sm font-medium text-amber-900">{selectedPr.advance_approved_by}</p>
                      </div>
                    )}
                    {selectedPr.advance_approved_at && (
                      <div>
                        <Label className="text-amber-700">PPA Approved At</Label>
                        <p className="text-sm font-medium text-amber-900">
                          {new Date(selectedPr.advance_approved_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedPr.advance_rejected_by && (
                      <div>
                        <Label className="text-red-700">PPA Rejected By</Label>
                        <p className="text-sm font-medium text-red-900">{selectedPr.advance_rejected_by}</p>
                      </div>
                    )}
                    {selectedPr.advance_rejection_reason && (
                      <div className="col-span-2">
                        <Label className="text-red-700">Rejection Reason</Label>
                        <p className="text-sm text-red-900 p-2 bg-red-50 rounded">{selectedPr.advance_rejection_reason}</p>
                      </div>
                    )}
                    {selectedPr.advance_paid_at && (
                      <div>
                        <Label className="text-green-700">Advance Paid On</Label>
                        <p className="text-sm font-medium text-green-900">
                          {new Date(selectedPr.advance_paid_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {selectedPr.advance_utr_number && (
                      <div>
                        <Label className="text-green-700">Advance UTR Number</Label>
                        <p className="text-sm font-medium text-green-900">{selectedPr.advance_utr_number}</p>
                      </div>
                    )}
                  </div>
                  <div className="bg-amber-100 p-3 rounded mt-2">
                    <p className="text-xs text-amber-800">
                      <strong>Note:</strong> This is an advance payment request. After advance is paid and delivery is confirmed, 
                      the pending amount (₹{((selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}) will require separate approval.
                    </p>
                  </div>
                </div>
              )}

              {/* Partial Payment Info Section - Show if any partial payments made */}
              {((selectedPr.partial_payments && selectedPr.partial_payments.length > 0) || (selectedPr.total_paid_amount && selectedPr.total_paid_amount > 0)) && (
                <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-amber-900">Partial Payment Info</h3>
                    <Badge className={selectedPr.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                      {selectedPr.payment_status === 'paid' ? 'Fully Paid' : 'Partly Paid'}
                    </Badge>
                  </div>
                  
                  {/* Balance Amount Summary */}
                  <div className="grid grid-cols-3 gap-4 bg-white p-3 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-600">Payable Amount</p>
                      <p className="text-sm font-bold text-gray-900">
                        ₹{(() => {
                          let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                          if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                            totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                          }
                          return totalPayable.toLocaleString();
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Total Paid</p>
                      <p className="text-sm font-bold text-green-700">
                        ₹{(selectedPr.total_paid_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Balance Amount</p>
                      <p className="text-sm font-bold text-amber-700">
                        ₹{(() => {
                          let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                          if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                            totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                          }
                          return (totalPayable - (selectedPr.total_paid_amount || 0)).toLocaleString();
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* Payment History */}
                  {selectedPr.partial_payments && selectedPr.partial_payments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-amber-900">Payment History:</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedPr.partial_payments.map((payment, index) => (
                          <div key={payment.id || index} className="bg-white p-3 rounded-lg border border-amber-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  Payment #{index + 1}: ₹{(payment.amount || 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-600">
                                  UTR: {payment.utr_number} | {payment.payment_method}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-600">
                                  {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'N/A'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  by {payment.paid_by}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Details */}
              {(selectedPr.tax_invoice_date || selectedPr.due_date) && (
                <div className="bg-green-50 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-gray-900">Invoice Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedPr.tax_invoice_date && (
                      <div>
                        <Label className="text-gray-600">Invoice Date</Label>
                        <p className="text-sm font-medium text-gray-900">{new Date(selectedPr.tax_invoice_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedPr.credit_period && (
                      <div>
                        <Label className="text-gray-600">Credit Period</Label>
                        <p className="text-sm font-medium text-gray-900">{selectedPr.credit_period} days</p>
                      </div>
                    )}
                    {selectedPr.due_date && (
                      <div>
                        <Label className="text-gray-600">Due Date</Label>
                        <p className="text-sm font-medium text-gray-900">{new Date(selectedPr.due_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedPr.paid_at && (
                      <div>
                        <Label className="text-gray-600">Paid On</Label>
                        <p className="text-sm font-medium text-gray-900">{new Date(selectedPr.paid_at).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedPr.utr_number && (
                      <div>
                        <Label className="text-gray-600">UTR Number</Label>
                        <p className="text-sm font-medium text-gray-900">{selectedPr.utr_number}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Hold Reason */}
              {selectedPr.hold_reason && (
                <div className="bg-red-50 rounded-lg p-4">
                  <Label className="text-red-700 font-semibold">Hold Reason</Label>
                  {selectedPr.hold_reason_type && (
                    <p className="text-sm font-medium text-red-800 mt-2">Type: {selectedPr.hold_reason_type}</p>
                  )}
                  <p className="text-sm text-gray-900 mt-2">{selectedPr.hold_reason}</p>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedPr.rejection_reason && (
                <div className="bg-red-50 rounded-lg p-4">
                  <Label className="text-red-700 font-semibold">Rejection Reason</Label>
                  <p className="text-sm text-gray-900 mt-2">{selectedPr.rejection_reason}</p>
                </div>
              )}

              {/* Documents */}
              <div className="space-y-2">
                <Label className="text-gray-600">Documents</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedPr.proforma_invoice_url && (
                    <Button
                      onClick={() => downloadFile(
                        selectedPr.proforma_invoice_url, 
                        getFilenameWithExtension(selectedPr.proforma_invoice_url, `Proforma_Invoice_${selectedPr.id}.pdf`)
                      )}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Proforma Invoice
                    </Button>
                  )}
                  {selectedPr.po_url && (
                    <Button
                      onClick={() => downloadFile(selectedPr.po_url, `PO_${selectedPr.po_number}.pdf`)}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Purchase Order
                    </Button>
                  )}
                  {selectedPr.tax_invoice_url && (
                    <Button
                      onClick={() => downloadFile(
                        selectedPr.tax_invoice_url, 
                        getFilenameWithExtension(selectedPr.tax_invoice_url, `Tax_Invoice_${selectedPr.id}.pdf`)
                      )}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Tax Invoice
                    </Button>
                  )}
                </div>
              </div>

              {/* Document Trail */}
              <div className="border-t pt-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Document Trail</h3>
                <div className="space-y-2">
                  {/* Purchase Request Created */}
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">Purchase Request Created</div>
                      <div className="text-xs text-gray-500">
                        by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'short', day: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Proforma Invoice Uploaded */}
                  {selectedPr.proforma_invoice_url && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Proforma Invoice Uploaded</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Function Approved */}
                  {selectedPr.approved_by && selectedPr.approved_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Function Approved</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.approved_by} on {new Date(selectedPr.approved_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PO Generated */}
                  {selectedPr.po_number && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">PO Generated</div>
                        <div className="text-xs text-gray-500">
                          PO #{selectedPr.po_number}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Delivery Confirmed */}
                  {selectedPr.delivery_confirmed && selectedPr.delivery_confirmed_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Delivery Confirmed</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.delivery_confirmed_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tax Invoice Uploaded */}
                  {selectedPr.tax_invoice_url && selectedPr.tax_invoice_date && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Tax Invoice Uploaded</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.tax_invoice_date).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Finance Approved */}
                  {selectedPr.finance_approved_by && selectedPr.finance_approved_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Finance Approved</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.finance_approved_by} on {new Date(selectedPr.finance_approved_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Completed */}
                  {selectedPr.paid_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Payment Completed</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.paid_by} on {new Date(selectedPr.paid_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hold/Unhold Dialog */}
      <Dialog open={holdDialogOpen} onOpenChange={setHoldDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'hold' ? 'Hold Purchase Request' : 'Unhold Purchase Request'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'hold' 
                ? 'This will put the request on hold and prevent further actions until unhold.' 
                : 'This will remove the hold and allow the request to proceed.'}
            </DialogDescription>
          </DialogHeader>

          {selectedPr && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label>Requested By</Label>
                  <p className="font-medium">{selectedPr.requested_by}</p>
                </div>
                <div>
                  <Label>Amount</Label>
                  <p className="font-medium">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                </div>
              </div>

              {actionType === 'hold' && (
                <>
                  <div>
                    <Label>Hold Reason Type <span className="text-red-500">*</span></Label>
                    <select
                      value={holdReasonType}
                      onChange={(e) => setHoldReasonType(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Hold Reason Type</option>
                      <option value="Management Hold">Management Hold</option>
                      <option value="Accounts Query">Accounts Query</option>
                      <option value="Requester Query">Requester Query</option>
                    </select>
                  </div>
                  <div>
                    <Label>Hold Reason <span className="text-red-500">*</span></Label>
                    <textarea
                      value={holdReason}
                      onChange={(e) => setHoldReason(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      rows={3}
                      placeholder="Enter reason for holding this request..."
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button
                  onClick={() => setHoldDialogOpen(false)}
                  variant="outline"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleHoldAction}
                  disabled={loading}
                  className={actionType === 'hold' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                >
                  {loading ? 'Processing...' : actionType === 'hold' ? 'Hold Request' : 'Unhold Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceDashboard;